/*
 * Thẻ liệu trình của lượt ở màn Lễ tân (A27): chọn thẻ hoặc dịch vụ lẻ, báo nợ và hết buổi,
 * số buổi còn lại sau khi trừ, hoàn buổi cho người có quyền. Lễ tân bấm Xong thì server tự trừ.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Input, Modal, Popconfirm, Tag, message } from "antd";
import { CreditCard } from "lucide-react";
import useAuthStore from "../../store/authStore";
import { cancelTreatmentUsage, chooseTreatmentLine, loadTreatment, type TreatmentCard, type TreatmentLine, type VisitTreatment } from "../AppTuVan/receptionApi";
import type { Visit } from "../AppTuVan/logic";

const vnd = (n: number) => `${n.toLocaleString("vi-VN")} đ`;

export interface TreatmentState {
  data?: VisitTreatment;
  /** Môi trường này chưa có API trừ buổi */
  missing?: boolean;
  /** Tải lỗi (máy chủ, mạng, quyền): báo để Lễ tân tải lại, không âm thầm ẩn khung */
  error?: string;
  reload: () => void;
}

export function useVisitTreatment(serverId: number | undefined, refreshKey: string): TreatmentState {
  const [st, setSt] = useState<{ id?: number; data?: VisitTreatment; missing?: boolean; error?: string }>({});
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!serverId) return undefined;
    let stale = false;
    loadTreatment(serverId).then((r) => {
      if (stale) return;
      const ok = r.ok && !!r.data;
      setSt({ id: serverId, data: ok ? r.data! : undefined, missing: r.status === 404, error: ok || r.status === 404 ? undefined : r.message || "Không tải được thẻ liệu trình" });
    });
    return () => { stale = true; };
  }, [serverId, refreshKey, tick]);
  const reload = useCallback(() => setTick((x) => x + 1), []);
  return st.id === serverId ? { data: st.data, missing: st.missing, error: st.error, reload } : { reload };
}

/** Thẻ và dịch vụ lượt đang gắn */
export const selectedLineOf = (t?: VisitTreatment): { card: TreatmentCard; line: TreatmentLine } | undefined => {
  if (t?.treatment_mode !== "card") return undefined;
  for (const card of t.cards) {
    const line = card.lines.find((l) => l.line_id === t.selected_line_id);
    if (line) return { card, line };
  }
  return undefined;
};

export default function TreatmentBlock({ v, tr }: { v: Visit; tr: TreatmentState }) {
  const canCancel = useAuthStore((s) => s.hasPermission("treatment.usage_cancel"));
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const t = tr.data;
  if (tr.error && v.serverId) {
    return (
      <div className="border-t border-slate-100 px-4 py-3 text-[13px] dark:border-slate-800">
        <Alert
          type="warning" showIcon message={`Thẻ liệu trình: ${tr.error}`}
          description="Chưa biết khách có thẻ hay lượt đã trừ buổi chưa. Tải lại trước khi bấm Xong hoặc trừ tay."
          action={<Button size="small" onClick={tr.reload}>Tải lại</Button>}
        />
      </div>
    );
  }
  if (!t || !v.serverId || (!t.cards.length && !t.usage && !t.no_session)) return null;
  const serverId = v.serverId;
  const finished = !!v.exec?.doneAt || v.status === "done";
  const sel = selectedLineOf(t);
  const debt = sel?.card.debt;

  const pick = async (lineId: number | null, ok: string) => {
    setBusy(true);
    const r = await chooseTreatmentLine(serverId, lineId);
    setBusy(false);
    if (r.ok) message.success(ok); else message.error(r.msg);
    tr.reload();
  };
  /** Lượt đã xong mà chưa trừ: chọn thẻ là trừ ngay nên hỏi lại */
  const choose = (card: TreatmentCard, line: TreatmentLine) => {
    const label = `${card.card_code} · ${line.service_name}`;
    if (!finished) { pick(line.line_id, `Đã chọn ${label}`); return; }
    Modal.confirm({
      title: `Trừ 1 buổi thẻ ${card.card_code}?`,
      content: `Lượt của ${v.cust.name} đã xong, chọn ${line.service_name} sẽ trừ buổi ngay.`,
      okText: "Trừ buổi", cancelText: "Huỷ",
      onOk: () => pick(line.line_id, `Đã trừ 1 buổi ${label}`),
    });
  };
  const cancelUsage = async () => {
    setBusy(true);
    const r = await cancelTreatmentUsage(serverId, reason.trim());
    setBusy(false);
    if (r.ok) { message.success("Đã hoàn buổi"); setReason(""); } else message.error(r.msg);
    tr.reload();
  };

  return (
    <div className="space-y-2 border-t border-slate-100 px-4 py-3 text-[13px] dark:border-slate-800">
      <div className="flex items-center gap-1.5 text-[13.5px] font-semibold text-slate-800 dark:text-slate-100"><CreditCard size={15} className="text-[#3e91ab]" />Thẻ liệu trình</div>

      {t.usage && (
        <Alert
          type="success" showIcon
          message={`Đã trừ 1 buổi thẻ ${t.usage.card_code} · ${t.usage.service_name}`}
          description={`Dịch vụ còn ${t.usage.line_remaining} buổi, thẻ còn ${t.usage.card_remaining} buổi.`}
          action={canCancel ? (
            <Popconfirm
              title="Hoàn lại buổi đã trừ?"
              description={<Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Lý do, VD: bấm Xong nhầm khách" className="!mt-1 !w-[240px]" aria-label="Lý do hoàn buổi" />}
              okText="Hoàn buổi" cancelText="Đóng" okButtonProps={{ danger: true, disabled: !reason.trim() }} onConfirm={cancelUsage}
            >
              <Button size="small" danger loading={busy}>Hoàn buổi</Button>
            </Popconfirm>
          ) : undefined}
        />
      )}
      {!t.usage && t.no_session && <Alert type="warning" showIcon message="Thẻ đã hết buổi" description="Yêu cầu khách nạp tiền: mua dịch vụ tại quầy hoặc mua lại liệu trình." />}
      {!t.usage && t.need_choice && !finished && <Alert type="warning" showIcon message="Khách có nhiều thẻ, hỏi khách làm dịch vụ nào rồi chọn bên dưới." />}
      {!t.usage && finished && t.treatment_mode !== "retail" && t.cards.length > 0 && <Alert type="warning" showIcon message="Lượt đã xong nhưng chưa trừ buổi. Chọn thẻ là trừ ngay; không cần trừ thì chọn Dịch vụ lẻ." />}
      {debt?.has_debt && !t.usage && (
        <Alert
          type={debt.blocks_exec ? "error" : "warning"} showIcon
          message={`Thẻ ${sel!.card.card_code} đang nợ ${vnd(debt.overdue_amount || debt.outstanding)}${debt.max_overdue_days ? `, quá hạn ${debt.max_overdue_days} ngày` : ""}`}
          description={debt.blocks_exec ? "Yêu cầu khách thanh toán thẻ này. Có thể chọn thẻ khác hoặc dịch vụ lẻ." : undefined}
        />
      )}

      {!t.usage && (
        <div className="space-y-1.5">
          {t.cards.flatMap((card) => card.lines.map((line) => {
            const on = t.treatment_mode === "card" && t.selected_line_id === line.line_id;
            const empty = line.available <= 0;
            return (
              <button
                key={`${card.card_id}-${line.line_id}`} type="button" disabled={busy || empty || on} aria-pressed={on} onClick={() => choose(card, line)}
                className={`flex w-full items-start justify-between gap-2 rounded-md border px-2.5 py-2 text-left transition-colors disabled:cursor-not-allowed ${on ? "border-[#3e91ab] bg-[#e5f3f6] dark:bg-[#1e3f49]" : "border-slate-200 enabled:hover:border-[#3e91ab] dark:border-slate-700"} ${empty ? "opacity-60" : ""}`}
              >
                <span className="min-w-0">
                  <span className="block font-medium text-slate-800 dark:text-slate-100">{line.service_name}</span>
                  <span className="block text-[11.5px] text-slate-500">{card.card_code} · {card.package_name}</span>
                </span>
                <span className="flex shrink-0 flex-col items-end gap-1">
                  <span className={`text-[12px] font-semibold ${empty ? "text-red-600" : "text-slate-700 dark:text-slate-200"}`}>{empty ? "Hết buổi" : `Còn ${line.available} buổi`}</span>
                  {card.debt?.has_debt && <Tag color="red" className="!m-0">Đang nợ</Tag>}
                </span>
              </button>
            );
          }))}
          <Button block size="small" type={t.treatment_mode === "retail" ? "primary" : "default"} ghost={t.treatment_mode === "retail"} disabled={busy || t.treatment_mode === "retail"} onClick={() => pick(null, "Đã chọn dịch vụ lẻ, lượt này không trừ thẻ")}>
            {t.treatment_mode === "retail" ? "Đang chọn: dịch vụ lẻ, không trừ thẻ" : "Dịch vụ lẻ, không trừ thẻ"}
          </Button>
          {sel && !finished && <div className="text-xs text-slate-500">Bấm Xong sẽ trừ 1 buổi {sel.line.service_name}, thẻ {sel.card.card_code}.</div>}
        </div>
      )}
    </div>
  );
}
